import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
    deleteAllNotifications,
    deleteNotification,
    getNotificationInbox,
    getNotificationUnreadCount,
    getNotificationUnseenCount,
    markAllNotificationsRead,
    markNotificationRead,
    markNotificationsSeen,
} from "@/api/notifications";
import { getUser } from "@/api/client";

export const qk = {
    inbox: (limit = 20) => ["notifications", "inbox", limit],
    unreadCount: ["notifications", "unreadCount"],
    unseenCount: ["notifications", "unseenCount"],
};

function updateInboxPages(old, updateItems) {
    if (!old?.pages) return old;
    return {
        ...old,
        pages: old.pages.map((page) => ({
            ...page,
            items: updateItems(Array.isArray(page?.items) ? page.items : []),
        })),
    };
}

export function useNotificationInbox(options = {}) {
    const uid = getUser()?.id;
    const { limit = 20, enabled = true, ...queryOptions } = options;

    return useInfiniteQuery({
        queryKey: qk.inbox(limit),
        queryFn: ({ pageParam = 0 }) => getNotificationInbox({ limit, offset: pageParam }),
        initialPageParam: 0,
        getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.nextOffset : undefined),
        enabled: !!uid && enabled,
        staleTime: 5 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: false,
        ...queryOptions,
    });
}

export function useNotificationUnreadCount(options = {}) {
    const uid = getUser()?.id;
    const { enabled = true, ...queryOptions } = options;

    return useQuery({
        queryKey: qk.unreadCount,
        queryFn: async () => {
            const response = await getNotificationUnreadCount();
            return Number(response?.unread || 0);
        },
        enabled: !!uid && enabled,
        staleTime: 60 * 1000,
        gcTime: 60 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        ...queryOptions,
    });
}

export function useNotificationUnseenCount(options = {}) {
    const uid = getUser()?.id;
    const { enabled = true, ...queryOptions } = options;

    return useQuery({
        queryKey: qk.unseenCount,
        queryFn: async () => {
            const response = await getNotificationUnseenCount();
            return Number(response?.unseen || 0);
        },
        enabled: !!uid && enabled,
        staleTime: 60 * 1000,
        gcTime: 60 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        ...queryOptions,
    });
}

export function useMarkNotificationsSeen() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: (ids) => markNotificationsSeen(ids),
        onSuccess: (_res, ids) => {
            const idSet = new Set((Array.isArray(ids) ? ids : []).map(String));
            const now = new Date().toISOString();
            let markedCount = 0;

            qc.setQueriesData({ queryKey: ["notifications", "inbox"] }, (old) =>
                updateInboxPages(old, (items) =>
                    items.map((item) => {
                        const itemId = String(item.id || item._id || "");
                        if (!itemId || !idSet.has(itemId) || item.seenAt) return item;
                        markedCount += 1;
                        return {
                            ...item,
                            id: item.id || item._id,
                            seenAt: now,
                        };
                    }),
                ),
            );

            if (markedCount > 0) {
                qc.setQueryData(qk.unseenCount, (old) => Math.max(0, Number(old || 0) - markedCount));
            }
            qc.invalidateQueries({ queryKey: qk.unseenCount });
        },
    });
}

export function useMarkNotificationRead() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: (id) => markNotificationRead(id),
        onSuccess: (_res, id) => {
            const now = new Date().toISOString();
            let markedUnseen = 0;
            qc.setQueriesData({ queryKey: ["notifications", "inbox"] }, (old) =>
                updateInboxPages(old, (items) =>
                    items.map((item) => {
                        if (!(item.id === id || item._id === id)) return item;
                        if (!item.seenAt) markedUnseen += 1;
                        return {
                            ...item,
                            id: item.id || item._id,
                            status: "read",
                            readAt: now,
                            seenAt: item.seenAt ?? now,
                        };
                    }),
                ),
            );
            qc.setQueryData(qk.unreadCount, (old) => Math.max(0, Number(old || 0) - 1));
            qc.invalidateQueries({ queryKey: qk.unreadCount });
            if (markedUnseen > 0) {
                qc.setQueryData(qk.unseenCount, (old) => Math.max(0, Number(old || 0) - markedUnseen));
            }
            qc.invalidateQueries({ queryKey: qk.unseenCount });
        },
    });
}

export function useMarkAllNotificationsRead() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: markAllNotificationsRead,
        onSuccess: () => {
            qc.setQueriesData({ queryKey: ["notifications", "inbox"] }, (old) => {
                const now = new Date().toISOString();
                return updateInboxPages(old, (items) =>
                    items.map((item) => ({
                        ...item,
                        id: item.id || item._id,
                        status: "read",
                        readAt: item.readAt ?? now,
                        seenAt: item.seenAt ?? now,
                    })),
                );
            });
            qc.setQueryData(qk.unreadCount, 0);
            qc.invalidateQueries({ queryKey: qk.unreadCount });
            qc.setQueryData(qk.unseenCount, 0);
            qc.invalidateQueries({ queryKey: qk.unseenCount });
        },
    });
}

export function useDeleteNotification() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: (id) => deleteNotification(id),
        onSuccess: (_res, id) => {
            qc.setQueriesData({ queryKey: ["notifications", "inbox"] }, (old) =>
                updateInboxPages(old, (items) => items.filter((item) => item.id !== id && item._id !== id)),
            );
            qc.invalidateQueries({ queryKey: qk.unreadCount });
            qc.invalidateQueries({ queryKey: qk.unseenCount });
        },
    });
}

export function useDeleteAllNotifications() {
    const qc = useQueryClient();

    return useMutation({
        mutationFn: deleteAllNotifications,
        onSuccess: () => {
            qc.removeQueries({ queryKey: ["notifications", "inbox"] });
            qc.setQueryData(qk.unreadCount, 0);
            qc.invalidateQueries({ queryKey: qk.unreadCount });
            qc.setQueryData(qk.unseenCount, 0);
            qc.invalidateQueries({ queryKey: qk.unseenCount });
        },
    });
}
